/// ///////////////////////////////////////////////////////////////////////////////
// Copyright (C) 2025 CONQUER INNOVATIONS SP. Z O.O. Global B.V. and its affiliate(s).
// All rights reserved.
//
// This software and other materials contain proprietary information
// controlled by CONQUER INNOVATIONS SP. Z O.O. and are protected by applicable copyright legislation.
// Any use and utilization of this software and other materials and
// disclosure to any third parties is conditional upon having a separate
// agreement with CONQUER INNOVATIONS SP. Z O.O. for the access, use, utilization or disclosure of this
// software. In the absence of such agreement, the use of the software is not
// allowed.
// The creator, author and originator of the code in the current file: Michał Pawłowski
//////////////////////////////////////////////////////////////////////////////////


<template>
  <div class="common-data-panel shadow-sm border rounded">
    <div v-if="dataIsEmpty">
      <div class="p-4 text-center text-muted fst-italic empty-panel">
        Wybierz zamówienie, aby wyświetlić jego szczegóły
      </div>
    </div>
    <div v-else>
      <div class="panel-header p-3 border-bottom bg-white">
        <div class="d-flex justify-content-between align-items-start flex-wrap gap-3">

          <div class="flex-grow-1">
            <h2 v-if="titleValue" class="panel-title mb-2">
              {{ titleValue }}
            </h2>
            <h2 v-else class="panel-title mb-2 text-muted fst-italic">
              Brak tytułu
            </h2>

            <div v-if="metaHeaders.length > 0" class="meta-row">
             <span v-for="(header, index) in metaHeaders" :key="'meta-'+index">
                        <span v-if="['date', 'datetime'].includes(header.type)">
                           <b>{{ header.text }}:</b> &nbsp;
                    <i class="far fa-calendar-alt me-1 opacity-50 small"></i>
                   {{ formatValue(localData[header.key], header) }}
                 </span>

                <span v-else-if="header.type === 'boolean'">
                   <b>{{ header.text }}:</b>&nbsp;
                    <span class="badge rounded-pill fw-normal"
                          :class="localData[header.key] ? 'bg-success-subtle text-success border border-success-subtle' : 'bg-danger-subtle text-danger border border-danger-subtle'">
                      {{ localData[header.key] ? "Tak" : "Nie" }}
                    </span>
                 </span>
               <span v-else>
                    <b>{{ header.text }}:</b> {{ formatValue(localData[header.key], header) }}
               </span>


             </span>
            </div>
          </div>

          <div v-if="actionHeaders.length > 0" class="action-bar d-flex flex-wrap gap-2 justify-content-end"
               :style="actionsBarStyle">

            <template v-for="(header, index) in actionHeaders" :key="'act-grp-'+index">
              <button
                  v-if="header.type === 'button'"
                  :class="mapButtonClass(header.class)"
                  class="btn btn-sm shadow-sm d-inline-flex align-items-center gap-1"
                  @click="triggerAction(header.action, localData)"
                  :title="header.tooltip"
              >
                <i v-if="header.icon" :class="header.icon"></i>
                <span v-if="header.text">{{ header.text }}</span>
              </button>

              <i
                  v-if="header.type === 'icon'"
                  :class="[header.icon, 'action-icon p-1 rounded cursor-pointer']"
                  @click="triggerAction(header.action, localData)"
                  :title="header.tooltip"
              ></i>

              <div v-if="header.type === 'actions'" class="btn-group btn-group-sm">

                <button
                    v-for="(act, aIdx) in header.actions"
                    :key="'sub-act-'+aIdx"
                    :class="mapButtonClass(act.class)"
                    class="btn btn-sm shadow-sm d-inline-flex align-items-center gap-1"
                    @click="triggerAction(act, localData)"
                    :disabled="!act.enabled"
                    :title="act.tooltip"
                    :style="act.style"
                >
                  <i v-if="act.icon" :class="act.icon"></i>
                  <span v-if="act.text">{{ act.text }}</span>
                </button>
              </div>

            </template>
          </div>

        </div>
      </div>

      <div v-if="dataHeaders.length > 0" class="panel-body p-3 bg-white">

        <div class="data-grid">

          <div
              v-for="(header, index) in dataHeaders"
              :key="'grid-item-'+index"
              class="data-item p-2 border rounded bg-light-gray-subtle"
              :style="getItemStyle(header)"
          >
            <div class="d-flex justify-content-between align-items-center">

               <span class="text-secondary fw-medium small d-flex align-items-center gap-1">
                 <i class="fas fa-level-down-alt select-value-action"
                    title="Wybierz wartość"
                    @dblclick.stop="triggerValue(header.key, localData[header.key])">
                 </i>
                 {{ header.text }}:
               </span>

              <b :class="getValueClass(header, localData[header.key])" class="text-end text-break ms-2">

                 <span v-if="['text', 'number', 'currency'].includes(header.type)">
                    {{ formatValue(localData[header.key], header) }}
                 </span>

                <span v-if="['date', 'datetime'].includes(header.type)">
                    <i class="far fa-calendar-alt me-1 opacity-50 small"></i>
                    {{ formatValue(localData[header.key], header) }}
                 </span>

                <span v-if="header.type === 'boolean'">
                    <span class="badge rounded-pill fw-normal"
                          :class="localData[header.key] ? 'bg-success-subtle text-success border border-success-subtle' : 'bg-danger-subtle text-danger border border-danger-subtle'">
                      {{ localData[header.key] ? "Tak" : "Nie" }}
                    </span>
                 </span>

                <span v-if="header.type === 'array' && Array.isArray(localData[header.key])">
                    {{ localData[header.key].join(', ') }}
                 </span>
                <span v-if="header.type === 'html'"
                      v-html="formatValue(localData[header.key], header)">
                </span>

                <span v-if="header.type === 'progress'"
                      class="d-flex align-items-center"
                      :style="{ minWidth: getProgressBarValidWidth(header.width) || '120px' }"
                >
                      <div class="progress flex-grow-1 shadow-sm"
                           style="height: 8px; background-color: #e9ecef; border-radius: 4px;">
                        <div class="progress-bar rounded"
                             role="progressbar"
                             :style="{
                                width: Math.min(localData[header.key] || 0, 100) + '%',
                                backgroundColor: getProgressColor(localData[header.key], header)
                             }"
                             :aria-valuenow="localData[header.key] || 0"
                             aria-valuemin="0"
                             aria-valuemax="100">
                        </div>
                      </div>

                  <span class="ms-2 small fw-bold text-muted"
                        style="min-width: 35px; text-align: right; font-size: 11px;">
                    {{ localData[header.key] || 0 }}%
                  </span>
</span>

              </b>
            </div>
          </div>

        </div>

      </div>
    </div>


  </div>
</template>

<script>
import dateFormat from "dateformat";
import {defineComponent} from 'vue'

export default defineComponent({
  name: "CommonDataPanel",
  props: {
    // The main data object (single item)
    data: {
      type: Object,
      required: true,
      default: () => ({})
    },
    // Configuration headers (same structure as CommonTable2)
    // Extra property supported: 'isMeta' (boolean) -> moves field to header section
    headers: {
      type: Array,
      required: true
    },
    // Key in 'data' to use as the main H2 title
    titleKey: {
      type: String,
      default: null
    },
    // Optional fallback title if key not found
    defaultTitle: {
      type: String,
      default: ""
    },
    // Custom style for the action bar container
    actionsBarStyle: {
      type: [String, Object],
      default: ""
    },
    maxGridColumns: {
      type: Number,
      default: null
    },
    allowGridItemWidth: {
      type: Boolean,
      default: false
    }
  },
  emits: ["onTriggerAction", "onTriggerValue"],
  computed: {
    localData() {
      return this.data || {};
    },
    titleValue() {
      if (this.titleKey && this.localData[this.titleKey]) {
        return this.localData[this.titleKey];
      }
      return this.defaultTitle;
    },
    // Converts headers object to array for iteration
    headersList() {
      if (Array.isArray(this.headers)) return this.headers;
      return Object.values(this.headers);
    },
    // Filter headers for the Meta section (under title)
    metaHeaders() {
      return this.headersList.filter(h => h.isMeta === true && !this.isActionType(h.type));
    },
    // Filter headers for the Action Bar (buttons, icons, actions)
    actionHeaders() {
      return this.headersList.filter(h => this.isActionType(h.type));
    },
    // Filter headers for the main Data Grid
    dataHeaders() {
      return this.headersList.filter(h => !h.isMeta && !this.isActionType(h.type));
    },
    dataIsEmpty() {
      return Object.keys(this.localData).length === 0;
    },
    gridContainerStyle() {
      // Tryb 1: Manualne szerokości (Flexbox)
      // Jeśli pozwalamy na zmianę szerokości, przełączamy się na Flexbox, aby % szerokości działały płynnie
      if (this.allowGridItemWidth) {
        return {
          display: 'flex',
          flexWrap: 'wrap',
          gap: '8px'
        };
      }

      // Tryb 2: Sztywna liczba kolumn (Grid)
      // Jeśli zdefiniowano maxGridColumns, nadpisujemy responsywność sztywnym podziałem
      if (this.maxGridColumns) {
        return {
          display: 'grid',
          gridTemplateColumns: `repeat(${this.maxGridColumns}, 1fr)`,
          gap: '8px'
        };
      }

      // Tryb 3: Domyślny (zostawiamy puste, zadziała klasa CSS .data-grid z auto-fill)
      return {};
    },
  },
  methods: {
    isActionType(type) {
      return ['button', 'icon', 'actions'].includes(type);
    },

    // --- Formatting Logic (copied/adapted from CommonTable2) ---
    formatValue(value, header) {
      if (value === null || value === undefined) return "-";

      if (header.type === 'currency') return this.formatCurrency(value);
      if (header.type === 'date') return this.getDate(value, header.isEpochMillis);
      if (header.type === 'datetime') return this.getDateTime(value, header.isEpochMillis);

      return value;
    },
    formatCurrency(amount) {
      return new Intl.NumberFormat('pl-PL', {style: 'currency', currency: 'PLN'}).format(amount);
    },
    getDateTime(v, e) {
      if (!e) return v;
      if (!v || v == 0) return "-";
      return dateFormat(new Date(v), "dd.mm.yyyy HH:MM:ss");
    },
    getDate(v, e) {
      if (!e) return v;
      if (!v || v == 0) return "-";
      return dateFormat(new Date(v), "dd.mm.yyyy");
    },

    // --- Styling Helpers ---
    getValueClass(header, value) {
      // Basic color logic based on type
      if (header.type === 'currency' || header.type === 'number') return 'text-primary';
      return 'text-dark';
    },
    mapButtonClass(className) {
      // Maps legacy or bootstrap classes to preserve look
      if (!className) return 'btn-outline-primary';
      return className;
    },

    // --- Events ---
    triggerAction(action, item) {
      this.$emit("onTriggerAction", {
        action: action,
        itemData: item
        // item key not needed as we have single object, but could be added if needed
      });
    },
    triggerValue(key, value) {
      this.$emit("onTriggerValue", {
        key: key,
        value: value
      });
    },
    getItemStyle(header) {
      // Jeśli włączono obsługę szerokości I nagłówek ma zdefiniowaną szerokość (np. "50%")
      var styles = {};
      if(header.style){
        styles = header.style;
      }
      if (this.allowGridItemWidth && header.width) {
        styles.width = header.width;
        styles.flex = `0 0 ${header.width}`;
        styles.maxWidth = header.width;
        return styles;
      }

      // Domyślne zachowanie (jeśli brak szerokości, element wypełnia dostępną przestrzeń w Flexboxie)
      if (this.allowGridItemWidth) {
        // return { flex: '1 0 auto',  header.style };
        return { flex: '1 0 auto', ...styles };
      }

      // Jeśli tryb standardowy - brak stylów inline (rządzi Grid)
      return styles;
    },
    getProgressBarValidWidth(width) {
      if (!width) return null;
      // Sprawdź, czy width jest w formacie "XXpx" lub "XX%"
      const pxMatch = width.match(/^(\d+)px$/);
      const percentMatch = width.match(/^(\d+)%$/);

      if (pxMatch) {
        const originalWidth = pxMatch[0].replace('px', '');
        return originalWidth - 88 + 'px'; // Odejmij 88px na padding, gdy width jest jednostkowy w px
      } else if (percentMatch) {
        return percentMatch[0]; // Zwróć oryginalną wartość w %
      }
      return null; // Nieprawidłowy format
    },
    getProgressColor(value, header) {
      const val = parseFloat(value) || 0;
      // Przykład użycia Custom Ranges w headerze:
      // {
      //   "text": "Poziom Ryzyka",
      //     "key": "risk",
      //     "type": "progress",
      //     "width": "150px",
      //     "ranges": [
      //   { "max": 20, "color": "#28a745" },  // Poniżej 20% - Zielony (Małe ryzyko)
      //   { "max": 50, "color": "#17a2b8" },  // Poniżej 50% - Niebieski
      //   { "max": 80, "color": "#fd7e14" }   // Poniżej 80% - Pomarańczowy
      // ],
      //     "defaultColor": "#dc3545"             // Powyżej 80% - Czerwony (Krytyczne)
      // }

      // 1. Obsługa Custom Ranges (jeśli zdefiniowane w headerze)
      // Oczekiwana struktura w headerze:
      // ranges: [ { max: 30, color: '#dc3545' }, { max: 60, color: 'orange' } ], defaultColor: 'green'
      if (header.ranges && Array.isArray(header.ranges)) {
        // Sortujemy rosnąco po 'max', żeby logikę sprawdzać po kolei
        const sortedRanges = [...header.ranges].sort((a, b) => a.max - b.max);

        for (const range of sortedRanges) {
          if (val < range.max) {
            return range.color;
          }
        }
        // Jeśli wartość przekracza wszystkie zakresy, zwracamy kolor domyślny lub zielony
        return header.defaultColor || '#198754';
      }

      // 2. Domyślna logika (Hardcoded Standard)
      // < 30% -> Czerwony (Bootstrap Danger)
      if (val < 30) return '#dc3545';

      // < 60% -> Żółty (Bootstrap Warning)
      if (val < 60) return '#ffc107';

      // >= 60% -> Zielony (Bootstrap Success)
      return '#198754';
    },
  }
});
</script>

<style scoped>
/* --- Font & Colors (Matching Source Components) --- */
.common-data-panel {
  font-family: "Segoe UI", Tahoma, sans-serif;
  font-size: 13px;
  color: #2b2b2b;
  background-color: #eff2f7;
  transition: all 0.2s ease;
}

/* Backgrounds */
.bg-light-gray {
  background-color: #f3f2f1; /* Panel Container bg from source */
}

.bg-light-gray-subtle {
  background-color: #f9f9f9; /* Grid item bg from source */
  border-color: #e1dfdd !important;
}

/* --- Header Section --- */
.panel-title {
  font-size: 18px;
  font-weight: 600;
  color: #0078D4; /* Brand Blue */
  margin: 0;
}

.meta-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  color: #3b3a39;
  font-size: 12.5px;
}

/* --- Action Bar --- */
.action-icon {
  font-size: 1.1rem;
  color: #0078D4;
}

.action-icon:hover {
  background-color: #e1dfdd;
}

/* --- Data Grid --- */
.data-grid {
  display: grid;
  /* Zmiana z auto-fit na auto-fill zapobiega rozciąganiu 2 elementów na 50% ekranu.
     Zmniejszono minmax z 280px na 240px - to pozwoli zmieścić ok. 4 elementy na typowym ekranie laptopa.
  */
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 8px; /* Zmniejszono odstęp z 12px na 8px */
}
.data-item {
  transition: background-color 0.2s;
}

.data-item:hover {
  background-color: #f0f0f0; /* Slight hover effect */
}

/* --- Typography Helpers --- */
.text-primary {
  color: #0078D4 !important;
  font-weight: 600;
}

.text-dark {
  color: #201f1e;
  font-weight: 600;
}

/* --- Interactive Icons --- */
.select-value-action {
  cursor: pointer;
  color: #0078D4;
  margin-right: 4px;
  opacity: 0.7;
  transition: opacity 0.2s;
}

.select-value-action:hover {
  opacity: 1;
}

/* Bootstrap 5-like utilities fallback */
.bg-success-subtle {
  background-color: #d1e7dd;
}

.text-success {
  color: #0a3622;
}

.bg-danger-subtle {
  background-color: #f8d7da;
}

.text-danger {
  color: #58151c;
}
.empty-panel{
  border-color: #eff2f7;
  border-style: solid;
  border-width: 4px;

}
</style>